import { useRef, useCallback, useEffect } from 'react';
import { uploadPhotoToSupabase } from '../utils/supabaseStorage';

interface PhotoToUpload {
  displayId: string;
  dataUrl: string;
  fileName: string;
  onSuccess: (url: string) => void;
  onError: (error: string) => void;
  retryCount: number;
}

const MAX_RETRIES = 3;
const BATCH_SIZE = 3; // Process 3 uploads at a time
const UPLOAD_DELAY = 500; // 500ms between batches

/**
 * Sanitize property name for use in file paths
 * Converts to lowercase, replaces spaces/special chars with hyphens, limits length
 */
const sanitizePropertyName = (propertyName: string | undefined): string => {
  if (!propertyName) return 'unnamed-property';
  
  return propertyName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')  // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '')       // Remove leading/trailing hyphens
    .substring(0, 50);              // Limit length to 50 chars
};

/**
 * Hook for managing background photo uploads to Supabase Storage
 * Uses Strategy 1: Progressive Enhancement
 * - Photos stored with compressed base64 for offline viewing
 * - Uploads to Supabase Storage in background
 * - Updates photo objects with CDN URLs on success
 * 
 * @param surveyId - Survey ID for organizing files
 * @param propertyName - Property address/name for better file organization (optional)
 */
export const useRetrofitPhotoUpload = (surveyId: string | undefined, propertyName?: string) => {
  const uploadQueueRef = useRef<PhotoToUpload[]>([]);
  const isProcessingRef = useRef(false);
  const ongoingUploadsRef = useRef<Set<string>>(new Set());

  // Process upload queue
  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || uploadQueueRef.current.length === 0 || !surveyId) {
      return;
    }

    isProcessingRef.current = true;

    try {
      // Process in batches
      const batch = uploadQueueRef.current.splice(0, BATCH_SIZE);
      console.log(`[RetrofitPhotoUpload] Processing batch of ${batch.length} photos`);

      // Upload photos in parallel within batch
      await Promise.all(
        batch.map(async (item) => {
          const uploadKey = item.displayId;
          
          if (ongoingUploadsRef.current.has(uploadKey)) {
            console.log(`[RetrofitPhotoUpload] Photo ${uploadKey} already uploading, skipping`);
            return;
          }

          ongoingUploadsRef.current.add(uploadKey);

          try {
            console.log(`[RetrofitPhotoUpload] Uploading photo ${item.fileName}`);
            
            // Upload to Supabase Storage (using retrofit-survey-images bucket)
            const url = await uploadPhotoToSupabase(
              item.dataUrl,
              item.fileName,
              'retrofit-survey-images'
            );

            console.log(`[RetrofitPhotoUpload] Successfully uploaded ${item.fileName}:`, url);
            item.onSuccess(url);
          } catch (error) {
            console.error(`[RetrofitPhotoUpload] Error uploading ${item.fileName}:`, error);
            
            // Retry logic
            if (item.retryCount < MAX_RETRIES) {
              item.retryCount++;
              uploadQueueRef.current.push(item);
              console.log(`[RetrofitPhotoUpload] Queued retry ${item.retryCount}/${MAX_RETRIES} for ${item.fileName}`);
            } else {
              const errorMessage = error instanceof Error ? error.message : 'Upload failed';
              item.onError(errorMessage);
            }
          } finally {
            ongoingUploadsRef.current.delete(uploadKey);
          }
        })
      );

      // Wait before next batch
      if (uploadQueueRef.current.length > 0) {
        await new Promise(resolve => setTimeout(resolve, UPLOAD_DELAY));
      }
    } finally {
      isProcessingRef.current = false;
      
      // Continue processing if queue has items
      if (uploadQueueRef.current.length > 0) {
        processQueue();
      }
    }
  }, [surveyId]);

  // Queue a photo for upload
  const queuePhotoUpload = useCallback((
    displayId: string,
    dataUrl: string,
    section: string, // e.g., 'property_front', 'property_rear'
    photoType: string, // e.g., 'frontElevationImage'
    onSuccess: (url: string) => void,
    onError: (error: string) => void
  ) => {
    if (!propertyName) {
      console.warn('[RetrofitPhotoUpload] Cannot queue upload: missing propertyName. Photo will be stored with dataUrl only (offline mode).');
      // Call onSuccess with empty url to indicate photo is stored but not uploaded
      onSuccess('');
      return;
    }

    // DEDUPLICATION: Check if this photo is already queued or uploading
    const isAlreadyQueued = uploadQueueRef.current.some(item => item.displayId === displayId);
    const isCurrentlyUploading = ongoingUploadsRef.current.has(displayId);
    
    if (isAlreadyQueued || isCurrentlyUploading) {
      console.log(`[RetrofitPhotoUpload] ⏭️ Photo ${displayId} already ${isCurrentlyUploading ? 'uploading' : 'in queue'}, skipping duplicate`);
      // Still call onSuccess to prevent UI blocking, but don't re-upload
      onSuccess('');
      return;
    }

    // Generate fileName with property name as primary identifier
    // Using displayId (without timestamp) for consistent naming and to allow upsert
    const sanitized = sanitizePropertyName(propertyName);
    // Path structure: property-name/section/photoType/displayId.jpg
    // Email will be prepended by uploadPhotoToSupabase: email/property-name/section/photoType/file.jpg
    const fileName = `${sanitized}/${section}/${photoType}/${displayId}.jpg`;

    // Add to queue
    uploadQueueRef.current.push({
      displayId,
      dataUrl,
      fileName,
      onSuccess,
      onError,
      retryCount: 0
    });

    console.log(`[RetrofitPhotoUpload] ✅ Queued photo ${fileName} for upload. Queue size: ${uploadQueueRef.current.length}`);

    // Start processing
    processQueue();
  }, [surveyId, propertyName, processQueue]);

  // Clear queue (useful for cleanup)
  const clearQueue = useCallback(() => {
    uploadQueueRef.current = [];
    ongoingUploadsRef.current.clear();
    isProcessingRef.current = false;
  }, []);

  // Get queue status
  const getQueueStatus = useCallback(() => {
    return {
      queueLength: uploadQueueRef.current.length,
      ongoingUploads: ongoingUploadsRef.current.size,
      isProcessing: isProcessingRef.current
    };
  }, []);

  return {
    queuePhotoUpload,
    clearQueue,
    getQueueStatus
  };
};
